import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import {
    A2HS_REQUIRED_EVENT,
    beginA2hsGate,
    isIosSafari,
    isPatientFacingPath,
    isStablePatientEntryPath,
    markA2hsGateDone,
    markPatientHomeScreenInstalled,
    needsOpenInChromeForHomeScreen,
    openPatientUrlInChrome,
    savePatientRoomEntry,
    shouldShowA2hsGate,
} from '../utils/patientRoomAccess';
import { promptPatientInstall } from '../utils/patientInstallPrompt';

/**
 * Hospital bedside gate — only Allow or Skip.
 * Mobile only; skipped when already installed as Home Screen app.
 */
const PatientEasyAccess = () => {
    const location = useLocation();
    const [gateOpen, setGateOpen] = useState(false);
    const [busy, setBusy] = useState(false);
    const ios = isIosSafari();

    const onRoomLanding = useMemo(() => {
        const path = location.pathname || '';
        if (path.indexOf('/customer/feedback') === 0) {
            return false;
        }
        return isStablePatientEntryPath(path) || path === '/patient-home';
    }, [location.pathname]);

    const openGate = useCallback(() => {
        if (!shouldShowA2hsGate()) {
            markA2hsGateDone();
            setGateOpen(false);
            return;
        }
        beginA2hsGate();
        setGateOpen(true);
    }, []);

    useEffect(() => {
        if (!isPatientFacingPath(location.pathname)) {
            return undefined;
        }
        if (location.pathname !== '/patient-home' && isStablePatientEntryPath(location.pathname)) {
            savePatientRoomEntry(location.pathname, '');
        }
        return undefined;
    }, [location.pathname]);

    useEffect(() => {
        if (!onRoomLanding) {
            setGateOpen(false);
            return undefined;
        }

        const fromFeedback = new URLSearchParams(location.search || '').get('fromFeedback') === '1';
        let postFeedback = false;
        try {
            postFeedback = sessionStorage.getItem('liveassist_post_feedback') === '1';
        } catch (_e) {
            postFeedback = false;
        }
        if (fromFeedback || postFeedback || !shouldShowA2hsGate()) {
            markA2hsGateDone();
            setGateOpen(false);
            return undefined;
        }

        openGate();
        return undefined;
    }, [onRoomLanding, location.pathname, location.search, openGate]);

    useEffect(() => {
        const onVendorFlag = () => {
            if (!shouldShowA2hsGate()) {
                markA2hsGateDone();
                setGateOpen(false);
                return;
            }
            if (onRoomLanding) {
                openGate();
            }
        };
        const onRequired = () => {
            if (onRoomLanding && shouldShowA2hsGate()) {
                openGate();
            } else {
                markA2hsGateDone();
                setGateOpen(false);
            }
        };
        window.addEventListener('liveassist-a2hs-vendor-flag', onVendorFlag);
        window.addEventListener(A2HS_REQUIRED_EVENT, onRequired);
        return () => {
            window.removeEventListener('liveassist-a2hs-vendor-flag', onVendorFlag);
            window.removeEventListener(A2HS_REQUIRED_EVENT, onRequired);
        };
    }, [onRoomLanding, openGate]);

    const finishGate = useCallback(() => {
        markA2hsGateDone();
        setGateOpen(false);
        setBusy(false);
    }, []);

    useEffect(() => {
        if (!gateOpen) {
            return undefined;
        }
        const onInstalled = () => {
            markPatientHomeScreenInstalled();
            finishGate();
        };
        window.addEventListener('appinstalled', onInstalled);
        return () => window.removeEventListener('appinstalled', onInstalled);
    }, [gateOpen, finishGate]);

    const onAllow = useCallback(async () => {
        if (busy) {
            return;
        }
        // Camera / Samsung / non-Chrome → open real Chrome first
        if (needsOpenInChromeForHomeScreen()) {
            const path = `${location.pathname || ''}${location.search || ''}`;
            openPatientUrlInChrome(`${window.location.origin}${path}`);
            return;
        }

        setBusy(true);
        try {
            if (!ios) {
                const result = await promptPatientInstall();
                if (result?.outcome === 'accepted') {
                    markPatientHomeScreenInstalled();
                }
            }
        } catch (_e) {
            /* ignore */
        } finally {
            // Always continue to call after Allow — hospital patients must not get stuck
            finishGate();
        }
    }, [busy, finishGate, ios, location.pathname, location.search]);

    const onSkip = useCallback(() => {
        finishGate();
    }, [finishGate]);

    if (!gateOpen) {
        return null;
    }

    return (
        <div className="liveassist-a2hs-gate" role="dialog" aria-modal="true" aria-labelledby="liveassist-a2hs-title">
            <div className="liveassist-a2hs-gate-card liveassist-a2hs-gate-simple">
                <img className="liveassist-a2hs-gate-logo" src="/logo192.png" alt="" />
                <h2 id="liveassist-a2hs-title">Save on Home Screen?</h2>
                <p className="liveassist-a2hs-gate-lead">
                    Tap Allow so you can call the nurse again without scanning.
                </p>
                <div className="liveassist-a2hs-gate-actions">
                    <button
                        type="button"
                        className="liveassist-a2hs-primary"
                        onClick={onAllow}
                        disabled={busy}
                    >
                        {busy ? 'Please wait…' : 'Allow'}
                    </button>
                    <button type="button" className="liveassist-a2hs-ghost" onClick={onSkip} disabled={busy}>
                        Skip — Continue to call
                    </button>
                </div>
            </div>
        </div>
    );
};

export default PatientEasyAccess;
